import React from 'react';
import {
  Title,
  Group,
  Badge,
  Button,
  Stepper,
  Stack,
  Text,
  Avatar,
  Card,
  Loader,
  Tooltip,
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PageContainer } from '../../../components/ui/PageContainer';
import { CycleInfoCard } from '../../../components/review360/CycleInfoCard';
import { NominationList } from '../../../components/review360/NominationList';
import { ProgressDashboard } from '../../../components/review360/ProgressDashboard';
import {
  useReview360Cycle,
  useTransitionReview360Status,
  useNominations,
} from '../../../hooks/useApi';
import { useAnalytics, AnalyticsEvents } from '../../../analytics';
import { showToast } from '../../../utils/toast';
import { ReviewCycleStatus } from '../../../types';

const STATUS_COLORS: Record<ReviewCycleStatus, string> = {
  draft: 'gray',
  nomination: 'yellow',
  in_progress: 'blue',
  completed: 'teal',
  archived: 'gray',
};

const STATUS_I18N: Record<ReviewCycleStatus, string> = {
  draft: 'review360.phases.draft',
  nomination: 'review360.phases.nomination',
  in_progress: 'review360.phases.inProgress',
  completed: 'review360.phases.completed',
  archived: 'review360.phases.archived',
};

const STATUS_STEP_MAP: Record<ReviewCycleStatus, number> = {
  draft: 0,
  nomination: 1,
  in_progress: 2,
  completed: 3,
  archived: 4,
};

export const Review360CycleDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { track } = useAnalytics();

  const { data: cycle, isLoading } = useReview360Cycle(id ?? '');
  const transitionStatus = useTransitionReview360Status();
  const { data: nominations = [] } = useNominations(id ?? '');

  const handleTransition = async (newStatus: string) => {
    if (!id) return;
    try {
      await transitionStatus.mutateAsync({ id, status: newStatus });
      track(AnalyticsEvents.REVIEW_360_CYCLE_STATUS_CHANGED, {
        cycleId: id,
        from: cycle?.status,
        to: newStatus,
      });
      showToast({
        title: t('common.success'),
        message: t('review360.statusTransitionSuccess'),
        color: 'teal',
      });
    } catch {
      showToast({
        title: t('common.error'),
        message: t('review360.statusTransitionError'),
        color: 'red',
      });
    }
  };

  // Check if all subjects have at least one approved rater
  const hasAllSubjectsApproved = (() => {
    if (!cycle?.subjects?.length) return false;
    const approvedBySubject = new Set<string>();
    for (const nom of nominations) {
      if (nom.status === 'approved') {
        const sid = typeof nom.subjectId === 'object' ? nom.subjectId._id : nom.subjectId;
        approvedBySubject.add(sid);
      }
    }
    return cycle.subjects.every((s) => approvedBySubject.has(s._id));
  })();

  if (isLoading) {
    return (
      <PageContainer>
        <Group justify="center" py="xl">
          <Loader size="md" />
        </Group>
      </PageContainer>
    );
  }

  if (!cycle) {
    return (
      <PageContainer>
        <Text c="dimmed" ta="center" py="xl">
          {t('common.error')}
        </Text>
      </PageContainer>
    );
  }

  const activeStep = STATUS_STEP_MAP[cycle.status] ?? 0;
  // Что можно в цикле, считает сервер (поле `can`), а не флаг admin и состав команд.
  const canManage = cycle.can?.manage === true;
  const canOpenReport = (subjectId: string) => !!cycle.can?.report.includes(subjectId);

  return (
    <PageContainer>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/review-360')}
              size="sm"
            >
              {t('review360.backToList')}
            </Button>
            <Title order={2}>{cycle.title}</Title>
            <Badge color={STATUS_COLORS[cycle.status]} variant="light" size="lg">
              {t(STATUS_I18N[cycle.status])}
            </Badge>
          </Group>
        </Group>

        {/* Stepper */}
        <Stepper active={activeStep} allowNextStepsSelect={false} size="sm">
          <Stepper.Step label={t('review360.phases.draft')} />
          <Stepper.Step label={t('review360.phases.nomination')} />
          <Stepper.Step label={t('review360.phases.inProgress')} />
          <Stepper.Step label={t('review360.phases.completed')} />
          <Stepper.Step label={t('review360.phases.archived')} />
        </Stepper>

        {/* Content by status */}
        {cycle.status === 'draft' && (
          <Stack gap="md">
            <CycleInfoCard cycle={cycle} />
            {canManage && (
              <Group justify="flex-end">
                <Button
                  onClick={() => handleTransition('nomination')}
                  loading={transitionStatus.isPending}
                >
                  {t('review360.startNomination')}
                </Button>
              </Group>
            )}
          </Stack>
        )}

        {cycle.status === 'nomination' && (
          <Stack gap="md">
            <NominationList
              cycleId={cycle._id}
              subjects={cycle.subjects ?? []}
              nominateFor={cycle.can?.nominate ?? []}
            />
            {canManage && (
              <Group justify="flex-end">
                <Tooltip label={t('review360.pending')} disabled={hasAllSubjectsApproved}>
                  <Button
                    onClick={() => handleTransition('in_progress')}
                    loading={transitionStatus.isPending}
                    disabled={!hasAllSubjectsApproved}
                  >
                    {t('review360.startCollection')}
                  </Button>
                </Tooltip>
              </Group>
            )}
          </Stack>
        )}

        {cycle.status === 'in_progress' && (
          <Stack gap="md">
            <ProgressDashboard cycleId={cycle._id} subjects={cycle.subjects ?? []} />
            {canManage && (
              <Group justify="flex-end">
                <Button
                  onClick={() => handleTransition('completed')}
                  loading={transitionStatus.isPending}
                >
                  {t('review360.completeCycle')}
                </Button>
              </Group>
            )}
          </Stack>
        )}

        {(cycle.status === 'completed' || cycle.status === 'archived') && (
          <Stack gap="md">
            <Card withBorder radius="md" p="md">
              <Stack gap="sm">
                <Text fw={600}>{t('review360.subjects')}</Text>
                {cycle.subjects?.map((subject) => (
                  <Group key={subject._id} justify="space-between">
                    <Group gap="sm">
                      <Avatar src={subject.avatar} radius="xl" size="sm">
                        {subject.name?.charAt(0)}
                      </Avatar>
                      <Text size="sm">{subject.name}</Text>
                    </Group>
                    {canOpenReport(subject._id) && (
                      <Button
                        variant="light"
                        size="xs"
                        onClick={() => navigate(`/review-360/${cycle._id}/report/${subject._id}`)}
                      >
                        {t('review360.report')}
                      </Button>
                    )}
                  </Group>
                ))}
              </Stack>
            </Card>
          </Stack>
        )}
      </Stack>
    </PageContainer>
  );
};
